import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Save, Eye, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Field, Panel, PageTitle } from "../../components/admin/Field";
import { ServicesEditor } from "../../components/admin/ServicesEditor";

const TABS = [
  ["inicio", "Início"], ["secoes", "Seções"], ["servicos", "Serviços"], ["contato", "Contato"],
  ["endereco", "Endereço"], ["whatsapp", "WhatsApp"], ["rodape", "Redes e rodapé"], ["seo", "SEO"],
];

const TabFields = ({ tab, f, set, setServices }) => {
  const F = (k, label, extra = {}) => <Field key={k} label={label} value={f[k]} onChange={set(k)} testid={`content-${k}`} {...extra} />;
  switch (tab) {
    case "inicio":
      return <Panel title="Banner principal" desc="A imagem do banner é escolhida em Galeria › Banner principal.">{F("hero_eyebrow", "Texto pequeno acima do título")}{F("hero_title", "Título principal")}{F("hero_subtitle", "Frase de apresentação", { textarea: true })}{F("btn_accommodations", "Botão: acomodações")}{F("btn_whatsapp", "Botão: WhatsApp")}{F("btn_nav_cta", "Botão do menu")}{F("btn_room", "Botão nas acomodações")}</Panel>;
    case "secoes":
      return <Panel title="Títulos e textos das seções">{F("accommodations_title", "Acomodações — título")}{F("accommodations_intro", "Acomodações — texto", { textarea: true })}{F("gallery_title", "Galeria — título")}{F("gallery_intro", "Galeria — texto", { textarea: true })}{F("location_title", "Localização — título")}{F("location_intro", "Localização — texto", { textarea: true })}{F("contact_title", "Contato — título")}{F("contact_intro", "Contato — texto", { textarea: true })}</Panel>;
    case "servicos":
      return <Panel title="Serviços e comodidades">{F("services_title", "Título da seção")}{F("services_intro", "Texto da seção", { textarea: true })}<ServicesEditor services={f.services} onChange={setServices} /></Panel>;
    case "contato":
      return <Panel title="Informações de contato">{F("phone_label", "Rótulo do telefone")}{F("phone", "Telefone de reservas", { inputMode: "tel" })}{F("email", "E-mail (opcional)", { type: "email", inputMode: "email" })}{F("contact_hours", "Horários de atendimento", { textarea: true, hint: "Ex.: Recepção: … (uma linha por item)" })}{F("contact_extra", "Outras formas de contato", { textarea: true })}</Panel>;
    case "endereco":
      return <Panel title="Endereço e mapa" desc="O mapa e o botão “Como chegar” usam este endereço.">{F("address_street", "Rua e número")}{F("address_district", "Bairro")}{F("address_city", "Cidade")}{F("address_state", "UF")}{F("address_cep", "CEP", { inputMode: "numeric" })}</Panel>;
    case "whatsapp":
      return <Panel title="WhatsApp" desc="Os botões abrem a conversa via link oficial wa.me — não é preciso API paga.">{F("whatsapp_number", "Número com código do país e DDD", { inputMode: "tel", placeholder: "5511999999999", hint: "Somente números: 55 (Brasil) + DDD + número. Deixe vazio para ocultar os botões de WhatsApp." })}{F("whatsapp_message", "Mensagem padrão", { textarea: true })}{F("whatsapp_room_message", "Mensagem das acomodações", { textarea: true, hint: "Use {acomodacao} onde o nome do quarto deve aparecer." })}{F("whatsapp_float_label", "Texto do botão flutuante (computador)")}</Panel>;
    case "rodape":
      return <Panel title="Redes sociais e rodapé">{F("social_instagram", "Instagram (link completo)", { placeholder: "https://instagram.com/..." })}{F("social_facebook", "Facebook", { placeholder: "https://facebook.com/..." })}{F("social_tiktok", "TikTok", { placeholder: "https://tiktok.com/@..." })}{F("social_youtube", "YouTube", { placeholder: "https://youtube.com/..." })}{F("footer_text", "Texto institucional do rodapé", { textarea: true })}{F("footer_note", "Nota final (ex.: CNPJ / razão social)")}</Panel>;
    case "seo":
      return <Panel title="SEO — Google e compartilhamento" desc="Título e descrição exibidos no Google e ao compartilhar o link.">{F("seo_title", "Título do site", { hint: `${(f.seo_title || "").length}/60 caracteres recomendados` })}{F("seo_description", "Descrição", { textarea: true, hint: `${(f.seo_description || "").length}/160 caracteres recomendados` })}</Panel>;
    default:
      return null;
  }
};

export default function Content() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("aba") || "inicio";
  const [f, setF] = useState(null);
  const [unpublished, setUnpublished] = useState(false);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/admin/settings").then((r) => {
      setF(r.data.draft);
      setUnpublished(r.data.has_unpublished);
    });
  }, []);
  if (!f) return <p className="text-zinc-500">Carregando…</p>;
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    const services = f.services.filter((s) => s.title.trim());
    const { data } = await api.put("/admin/settings", { ...f, services });
    setF(data.draft);
  };

  const run = (kind) => async () => {
    setBusy(kind);
    const win = kind === "preview" ? window.open("", "_blank") : null;
    try {
      await save();
      if (kind === "save") {
        setUnpublished(true);
        toast.success("Alterações salvas como rascunho. Publique para exibir no site.");
      } else if (kind === "preview") {
        setUnpublished(true);
        if (win) win.location.href = "/?preview=1";
      } else {
        await api.post("/admin/settings/publish");
        setUnpublished(false);
        toast.success("Alterações publicadas no site!");
      }
    } catch (e) {
      if (win) win.close();
      toast.error(errMsg(e, "Não foi possível salvar."));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="admin-content-form">
      <PageTitle title="Editor de conteúdo" desc="Edite os textos do site. Salve como rascunho, visualize e publique quando estiver pronto." />
      {unpublished && <p data-testid="content-unpublished-badge" className="mb-4 text-sm text-[#E6C687]">• Existem alterações não publicadas.</p>}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-6 -mx-4 px-4 sm:mx-0 sm:px-0">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setParams({ aba: k })} data-testid={`content-tab-${k}`} className={`shrink-0 h-11 px-5 rounded-full text-sm border ${tab === k ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-zinc-700 text-zinc-300"}`}>{l}</button>
        ))}
      </div>
      <TabFields tab={tab} f={f} set={set} setServices={set("services")} />
      <div className="fixed md:sticky bottom-16 md:bottom-0 inset-x-0 z-20 glass border-t border-[#D4AF37]/15 md:border md:rounded-lg md:mt-8 p-3">
        <div className="grid grid-cols-3 gap-2 max-w-6xl mx-auto">
          <button onClick={run("save")} disabled={!!busy} data-testid="content-save-btn" aria-label="Salvar alterações" className="h-12 rounded-full border border-zinc-600 text-sm inline-flex items-center justify-center gap-2 hover:border-[#D4AF37]">
            {busy === "save" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} <span className="hidden min-[400px]:inline">Salvar</span><span className="hidden sm:inline"> alterações</span>
          </button>
          <button onClick={run("preview")} disabled={!!busy} data-testid="content-preview-btn" aria-label="Visualizar site" className="h-12 rounded-full border border-zinc-600 text-sm inline-flex items-center justify-center gap-2 hover:border-[#D4AF37]">
            {busy === "preview" ? <Loader2 size={16} className="animate-spin" /> : <Eye size={16} />} <span className="hidden min-[400px]:inline">Visualizar</span><span className="hidden sm:inline"> site</span>
          </button>
          <button onClick={run("publish")} disabled={!!busy} data-testid="content-publish-btn" className="btn-gold !px-2 h-12">
            {busy === "publish" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Publicar
          </button>
        </div>
      </div>
    </div>
  );
}
